import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ComplaintsModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    relationship: "",
    complaintType: "",
    complaint: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/complaints`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        relationship: formData.relationship,
        complaint_type: formData.complaintType,
        complaint: formData.complaint
      });

      if (response.status === 201) {
        toast.success("Your complaint has been submitted. We will respond within 48 hours.");
        setFormData({
          name: "",
          phone: "",
          email: "",
          relationship: "",
          complaintType: "",
          complaint: ""
        });
        onClose();
      }
    } catch (error) {
      console.error("Error submitting complaint:", error);
      toast.error("Something went wrong. Please try calling us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        data-testid="complaints-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6" />
            Submit a Complaint
          </DialogTitle>
        </DialogHeader>

        <div className="bg-orange-50 rounded-lg p-4 mb-4 border border-orange-200">
          <p className="text-sm text-orange-800">
            We take all complaints seriously and aim to resolve issues promptly. 
            Your feedback helps us improve our care services. We will respond within 48 hours.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="complaint-name">Your Name *</Label>
            <Input
              id="complaint-name"
              data-testid="complaint-name-input"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="John Smith"
              required
              className="mt-1"
            />
          </div>

          {/* Phone & Email Row */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="complaint-phone">Phone Number *</Label>
              <Input
                id="complaint-phone"
                data-testid="complaint-phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder="0151 234 5678"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="complaint-email">Email Address *</Label>
              <Input
                id="complaint-email"
                data-testid="complaint-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="john@example.com"
                required
                className="mt-1"
              />
            </div>
          </div>

          {/* Relationship & Type Row */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="complaint-relationship">Your Relationship</Label>
              <Select
                value={formData.relationship}
                onValueChange={(value) => handleChange("relationship", value)}
              >
                <SelectTrigger className="mt-1" data-testid="complaint-relationship-select">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Resident">Resident</SelectItem>
                  <SelectItem value="Family Member">Family Member</SelectItem>
                  <SelectItem value="Friend">Friend</SelectItem>
                  <SelectItem value="Healthcare Professional">Healthcare Professional</SelectItem>
                  <SelectItem value="Staff Member">Staff Member</SelectItem>
                  <SelectItem value="Visitor">Visitor</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="complaint-type">Complaint Type *</Label>
              <Select
                value={formData.complaintType}
                onValueChange={(value) => handleChange("complaintType", value)}
                required
              >
                <SelectTrigger className="mt-1" data-testid="complaint-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Care Quality">Care Quality</SelectItem>
                  <SelectItem value="Staff Conduct">Staff Conduct</SelectItem>
                  <SelectItem value="Communication">Communication</SelectItem>
                  <SelectItem value="Facilities">Facilities</SelectItem>
                  <SelectItem value="Food & Nutrition">Food & Nutrition</SelectItem>
                  <SelectItem value="Billing">Billing</SelectItem>
                  <SelectItem value="Safety Concern">Safety Concern</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Complaint Details */}
          <div>
            <Label htmlFor="complaint-details">Complaint Details *</Label>
            <Textarea
              id="complaint-details"
              data-testid="complaint-details-input"
              value={formData.complaint}
              onChange={(e) => handleChange("complaint", e.target.value)}
              placeholder="Please describe your complaint in detail. Include dates, times, and names if relevant..."
              rows={5}
              required
              className="mt-1"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            data-testid="complaint-submit-btn"
            disabled={isSubmitting}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
          >
            {isSubmitting ? (
              "Submitting..."
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Submit Complaint
              </>
            )}
          </Button>

          <p className="text-sm text-gray-500 text-center">
            We will acknowledge your complaint within 48 hours and aim to resolve all issues within 28 days.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
